import React, { useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import Icon from '../components/Icon';
import ThemeToggle from '../components/ThemeToggle';
import './Dashboard.css';
import { useApi } from '../services/apiService';
import { Account } from '../types/Account';
import OverviewStatCard from '../components/OverviewStatCard';
import AccountsPanel from '../components/AccountsPanel';
import TransactionsTable from '../components/TransactionsTable';
import CategoryPieChart from '../components/CategoryPieChart';
import TransactionList from '../components/TransactionList';
import AssetAllocationChart from '../components/AssetAllocationChart';
import { isRemoved } from '../utils/accountStatus';

const Dashboard: React.FC = () => {
  const { logout, user } = useAuth0();
  const [accounts, setAccounts] = useState<Account[] | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  // Bumping this remounts every data section so each refetches.
  const [refreshKey, setRefreshKey] = useState<number>(0);
  const { getAccounts, refreshTransactions } = useApi();
  const [selectedCategory, setSelectedCategory] =
    useState<string>('Uncategorized');

  const handleCategorySelect = (category: string) => {
    setSelectedCategory(category);
  };

  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const data = await getAccounts();
        setAccounts(data);
        setError(null);
      } catch (err) {
        setError('Failed to fetch account data.');
      } finally {
        setLoading(false);
      }
    };

    fetchAccounts();
  }, [refreshKey]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refreshTransactions();
    } catch (err) {
      console.error('Failed to refresh transactions:', err);
    } finally {
      setRefreshing(false);
      setRefreshKey((key) => key + 1);
    }
  };

  const activeAccounts = useMemo(
    () => (accounts ?? []).filter((account) => !isRemoved(account)),
    [accounts],
  );

  const handleAccountsChanged = () => setRefreshKey((key) => key + 1);

  const initials = (user?.name ?? user?.email ?? '?')
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');

  return (
    <div className="dashboard">
      <header className="dashboard-topbar">
        <div className="dashboard-topbar-inner">
          <div className="brand">
            <img
              src={`${process.env.PUBLIC_URL}/logo192.png`}
              alt="Kelvin's Finance App"
              className="brand-mark"
            />
          </div>
          <nav className="dashboard-nav" aria-label="Main">
            <a href="#overview" className="active">
              Overview
            </a>
            <a href="#allocation">Allocation</a>
            <a href="#accounts">Accounts</a>
            <a href="#spending">Spending</a>
            <a href="#transactions">Transactions</a>
          </nav>
          <div className="dashboard-user">
            <span className="avatar" title={user?.name ?? user?.email}>
              {initials}
            </span>
            <ThemeToggle />
            <button
              className="btn"
              onClick={() =>
                logout({ logoutParams: { returnTo: window.location.origin } })
              }
            >
              <Icon name="logOut" size={16} />
              Log out
            </button>
          </div>
        </div>
      </header>

      <main className="dashboard-main">
        <div id="overview" className="dashboard-heading">
          <div>
            <h1>Overview</h1>
            {accounts && (
              <p className="muted">
                {activeAccounts.length} account
                {activeAccounts.length === 1 ? '' : 's'} connected via Akahu
              </p>
            )}
          </div>
          <button className="btn" onClick={handleRefresh} disabled={refreshing}>
            <Icon
              name="refresh"
              size={16}
              className={refreshing ? 'spin' : undefined}
            />
            {refreshing ? 'Refreshing…' : 'Refresh data'}
          </button>
        </div>

        {loading && <p className="muted">Loading account data...</p>}
        {error && <p className="error">{error}</p>}
        {accounts && (
          <>
            <OverviewStatCard key={refreshKey} accounts={activeAccounts} />

            <div className="dashboard-row">
              <div id="allocation" className="dashboard-col-wide">
                <AssetAllocationChart key={refreshKey} />
              </div>
              <AccountsPanel
                accounts={accounts}
                onChanged={handleAccountsChanged}
              />
            </div>

            <div id="spending" className="dashboard-row">
              <div className="dashboard-col-narrow">
                <CategoryPieChart
                  key={refreshKey}
                  selectedCategory={selectedCategory}
                  onCategorySelect={handleCategorySelect}
                />
              </div>
              <div className="dashboard-col-wide">
                <TransactionList key={refreshKey} category={selectedCategory} />
              </div>
            </div>

            <div id="transactions">
              <TransactionsTable key={refreshKey} />
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default Dashboard;
